import { useFlashPCBSettings } from "../../../utils/hooks/commonly-stored/flashPCBSettings";
import {
  Board,
  BoardMountTypes,
  Shipment,
  ShipmentPart,
} from "../../../reducers/uploadedBoard";
import {
  BoardNotes,
  CardLabel,
  CardValue,
  GridCard,
  GridTable,
  PanelDetailsContainer,
  PartOrContainerTypeWarning,
  PartWarningsList,
  StencilDetailsContainer,
  TitleWarning,
} from "./BoardFilesStyles";
import CustomerMessages from "../../ReviewBoardFlow/Quote/CustomerMessages";
import {
  getBoundingBoxHeight,
  getBoundingBoxWidth,
  getMinimumHoleDiameterText,
  getNumLayers,
  substrateIsInstantQuoteValue,
  surfaceFinishIsInstantQuoteValue,
  viaHandlingIsInstantQuoteValue,
} from "../../ReviewBoardFlow/ProjectDetails/PhysicalDetails";
import { SubTitle } from "../parts/Bins/BinsStyles";
import Icon from "../../common/Icon";
import { useContext } from "react";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "../../../utils/hooks/commonly-stored/uploaded-board/state-machine/reviewBoardFlowLoader";
import useShipmentsFromContext from "../../../utils/hooks/commonly-stored/uploaded-board/shipments";
import LoadingIcon from "../../common/LoadingIcon";

const minHoleDiameterIsStandard = (minHoleDiameter: number | null) => {
  if (minHoleDiameter === null) {
    return true;
  } else {
    return minHoleDiameter >= 0.3;
  }
};

export function TrayPartsWarning({
  shipmentParts,
}: {
  shipmentParts: ShipmentPart[];
}) {
  const maybeTrayParts = shipmentParts.filter(
    (shipmentPart) => shipmentPart.part_may_require_a_tray,
  );
  if (maybeTrayParts.length > 0) {
    return (
      <>
        <TitleWarning>
          <Icon src={"/icons/alert_yellow.svg"} alt={"warn"} />
          <SubTitle>Board has parts that may require trays</SubTitle>
          <Icon src={"/icons/alert_yellow.svg"} alt={"warn"} />
        </TitleWarning>
        <PartWarningsList>
          {maybeTrayParts.map((shipmentPart) => (
            <PartOrContainerTypeWarning
              key={shipmentPart.part_id}
            >{`${shipmentPart.part_name} (${shipmentPart.element_names.join(",")})`}</PartOrContainerTypeWarning>
          ))}
        </PartWarningsList>
      </>
    );
  } else {
    return null;
  }
}

export function LithiumPartsWarning({
  shipmentParts,
}: {
  shipmentParts: ShipmentPart[];
}) {
  const lithiumParts = shipmentParts.filter(
    (shipmentPart) => shipmentPart.contains_lithium,
  );
  if (lithiumParts.length > 0) {
    return (
      <>
        <TitleWarning>
          <Icon src={"/icons/alert_yellow.svg"} alt={"warn"} />
          <SubTitle>
            Board has lithium batteries that cannot go through the oven
          </SubTitle>
          <Icon src={"/icons/alert_yellow.svg"} alt={"warn"} />
        </TitleWarning>
        <PartWarningsList>
          {lithiumParts.map((shipmentPart) => (
            <PartOrContainerTypeWarning
              key={shipmentPart.part_id}
            >{`${shipmentPart.part_name} (${shipmentPart.element_names.join(",")})`}</PartOrContainerTypeWarning>
          ))}
        </PartWarningsList>
      </>
    );
  } else {
    return null;
  }
}

export default function PanelDetails({ board }: { board: Board }) {
  const { shipmentId } = useContext(BoardIdContext) as BoardShipmentIds;
  const shipments = useShipmentsFromContext(board.id) as Shipment[];
  const flashPCBSettings = useFlashPCBSettings();
  if (shipments === null) {
    return <LoadingIcon text={"Loading shipments..."} />;
  }
  const leadTimeDays = (shipments[0] as Shipment).base_lead_time_days;
  if (!board.physical_details || !flashPCBSettings) return null;
  const shipment = shipments.find((shipment) => shipment.id === shipmentId);
  function renderDetailCard(
    label: string,
    value: React.ReactNode,
    isStandard: boolean,
  ) {
    return (
      <GridCard highlight={!isStandard}>
        <CardLabel highlight={!isStandard}>{label}</CardLabel>
        <CardValue highlight={!isStandard}>
          {value}
          {!isStandard && (
            <>
              <>{"  (non-standard)"}</>
              <Icon src={"/icons/alert_yellow.svg"} alt={"warn"} />
            </>
          )}
        </CardValue>
      </GridCard>
    );
  }

  function renderPasteInHoleWarnings() {
    if (shipment) {
      const pasteInHole = shipment.shipment_parts.filter(
        (shipmentPart) => shipmentPart.paste_in_hole,
      );
      if (pasteInHole.length > 0) {
        return (
          <>
            <TitleWarning>
              <Icon src={"/icons/alert_yellow.svg"} alt={"warn"} />
              <SubTitle>Board has paste in hole parts</SubTitle>
              <Icon src={"/icons/alert_yellow.svg"} alt={"warn"} />
            </TitleWarning>
            <PartWarningsList>
              {pasteInHole.map((shipmentPart) => (
                <PartOrContainerTypeWarning
                  key={shipmentPart.part_id}
                >{`${shipmentPart.part_name} (${shipmentPart.element_names.join(",")})`}</PartOrContainerTypeWarning>
              ))}
            </PartWarningsList>
          </>
        );
      } else {
        return null;
      }
    }
  }

  function renderCannotCleanWarnings() {
    if (shipment) {
      const cannotCleanParts = shipment.shipment_parts.filter(
        (shipmentPart) => shipmentPart.cannot_clean,
      );
      if (cannotCleanParts.length > 0) {
        return (
          <>
            <TitleWarning>
              <Icon src={"/icons/alert_yellow.svg"} alt={"warn"} />
              <SubTitle>Board has parts that cannot be cleaned:</SubTitle>
              <Icon src={"/icons/alert_yellow.svg"} alt={"warn"} />
            </TitleWarning>
            <PartWarningsList>
              {cannotCleanParts.map((shipmentPart) => (
                <PartOrContainerTypeWarning
                  key={shipmentPart.part_id}
                >{`${shipmentPart.part_name} (${shipmentPart.element_names.join(",")})`}</PartOrContainerTypeWarning>
              ))}
            </PartWarningsList>
          </>
        );
      } else {
        return null;
      }
    }
  }

  function renderLithiumPartsWarning() {
    if (shipment) {
      return <LithiumPartsWarning shipmentParts={shipment.shipment_parts} />;
    }
  }

  function renderTrayPartsWarning() {
    if (shipment) {
      return <TrayPartsWarning shipmentParts={shipment.shipment_parts} />;
    }
  }
  const INNER_COPPER_WEIGHT_DEFAULT = 0.5;
  const OUTER_COPPER_WEIGHT_DEFAULT = 1.0;
  return (
    <PanelDetailsContainer>
      <SubTitle>Panel details:</SubTitle>
      <GridTable>
        {renderDetailCard(
          "Number of layers",
          getNumLayers(board.physical_details),
          true,
        )}
        {renderDetailCard(
          "Bounding box width",
          getBoundingBoxWidth(board.physical_details),
          true,
        )}
        {renderDetailCard(
          "Bounding box height",
          getBoundingBoxHeight(board.physical_details),
          true,
        )}
        {renderDetailCard(
          "Minimum hole diameter",
          getMinimumHoleDiameterText(board.physical_details),
          minHoleDiameterIsStandard(board.physical_details.min_hole_diameter),
        )}
        {renderDetailCard(
          "Solder mask color",
          board.physical_details.soldermask_color,
          board.physical_details.soldermask_color === "Green",
        )}
        {renderDetailCard(
          "Inner copper weight",
          `${board.physical_details.inner_copper_weight} ${board.physical_details.copper_weight_unit}`,
          board.physical_details.inner_copper_weight ===
            INNER_COPPER_WEIGHT_DEFAULT,
        )}
        {renderDetailCard(
          "Outer copper weight",
          `${board.physical_details.outer_copper_weight} ${board.physical_details.copper_weight_unit}`,
          board.physical_details.outer_copper_weight ===
            OUTER_COPPER_WEIGHT_DEFAULT,
        )}
        {renderDetailCard(
          "Substrate",
          board.physical_details.substrate,
          substrateIsInstantQuoteValue(
            flashPCBSettings,
            board.physical_details.substrate,
          ),
        )}
        {renderDetailCard(
          "Board thickness",
          `${board.physical_details.board_thickness} ${board.physical_details.board_thickness_unit}`,
          board.physical_details.board_thickness === 1.6,
        )}
        {renderDetailCard(
          "Surface finish",
          board.physical_details.surface_finish,
          surfaceFinishIsInstantQuoteValue(
            flashPCBSettings,
            board.physical_details.surface_finish,
            leadTimeDays,
          ),
        )}
        {renderDetailCard(
          "Vias",
          board.physical_details.vias,
          viaHandlingIsInstantQuoteValue(
            flashPCBSettings,
            board.physical_details.vias,
          ),
        )}
      </GridTable>
      <StencilDetailsContainer>
        <SubTitle>Stencil details:</SubTitle>
        {renderDetailCard(
          "Board mount types",
          board.manufacturing_details.stencil_details.board_mount_types,
          board.manufacturing_details.stencil_details.board_mount_types ===
            BoardMountTypes.TOP_AND_BOTTOM,
        )}
      </StencilDetailsContainer>
      {renderPasteInHoleWarnings()}
      {renderTrayPartsWarning()}
      {renderCannotCleanWarnings()}
      {renderLithiumPartsWarning()}
      {board.manufacturing_details.customer_specifications.notes !== "" && (
        <BoardNotes>
          <SubTitle>Notes:</SubTitle>
          <div>{board.manufacturing_details.customer_specifications.notes}</div>
        </BoardNotes>
      )}
      <CustomerMessages />
    </PanelDetailsContainer>
  );
}
